import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { librariesApi, TimberLibraryItem } from '@structura/services'
import { steelGrades, concreteClasses } from '../materialConfig'
import { MATERIAL_PRESETS, calculateSelfWeightFromDensity } from '../constants'
import { calculateSectionProperties } from '../utils/sectionProperties'
import { CrossSection, MaterialFactorsDisplay, MaterialPresets, SectionMaterialStrength, SMBeamModel } from '../types'

type MaterialType = 'steel' | 'concrete' | 'timber'
export type TimberKind = 'glulam' | 'solid'
// Whether the timber design lookup may run for the selected kind/class pair (see the auto-fetch
// effect): 'pending' while the catalogue loads or the class is being reconciled to the kind,
// 'error' when it never can proceed.
type TimberSelectionStatus = { state: 'ready' } | { state: 'pending' } | { state: 'error'; message: string }
type NationalAnnex = 'Standard' | 'Norwegian'
type SteelGrade = 'S235' | 'S275' | 'S355' | 'S420' | 'S460'
type ConcreteClass =
  | 'C12/15' | 'C16/20' | 'C20/25' | 'C25/30' | 'C30/37' | 'C35/45'
  | 'C40/50' | 'C45/55' | 'C50/60' | 'C55/67' | 'C60/75'
  | 'C70/85' | 'C80/95' | 'C90/105'
type ReinforcementClass = 'B500NC' | 'B500NB' | 'B500A' | 'B500B' | 'B500C'
type PrestressClass = 'Y1770' | 'Y1860'
// NS-EN 1991-1-1 §6.3 imposed-load (occupancy) categories; selects the ψ row of
// NS-EN 1990 Tabell NA.A1.1 backend-side (codes DB).
export type ImposedLoadCategory = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H'

export interface UseMaterialStateResult {
  currentMaterial: MaterialType
  nationalAnnex: NationalAnnex
  setNationalAnnex: React.Dispatch<React.SetStateAction<NationalAnnex>>
  selectedSteelGrade: SteelGrade
  setSelectedSteelGrade: React.Dispatch<React.SetStateAction<SteelGrade>>
  selectedSteelSubgrade: string
  setSelectedSteelSubgrade: React.Dispatch<React.SetStateAction<string>>
  steelSubgrades: string[]
  steelBands: number[]
  steelThickness: number | ''
  setSteelThickness: React.Dispatch<React.SetStateAction<number | ''>>
  selectedConcreteClass: ConcreteClass
  setSelectedConcreteClass: React.Dispatch<React.SetStateAction<ConcreteClass>>
  selectedReinfClass: ReinforcementClass
  setSelectedReinfClass: React.Dispatch<React.SetStateAction<ReinforcementClass>>
  selectedPrestressClass: PrestressClass
  setSelectedPrestressClass: React.Dispatch<React.SetStateAction<PrestressClass>>
  timberKind: TimberKind
  selectTimberKind: (kind: TimberKind) => void
  selectedTimberClass: string
  setSelectedTimberClass: React.Dispatch<React.SetStateAction<string>>
  timberOptions: TimberLibraryItem[]
  timberKmod: number
  setTimberKmod: React.Dispatch<React.SetStateAction<number>>
  timberKmodTable: Record<string, number>
  timberLoadDuration: 'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'
  setTimberLoadDuration: React.Dispatch<React.SetStateAction<'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'>>
  timberServiceClass: 1 | 2 | 3
  setTimberServiceClass: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
  timberCreepFactor: number
  concreteRelativeHumidity: number
  setConcreteRelativeHumidity: React.Dispatch<React.SetStateAction<number>>
  concreteNotionalSize: number
  setConcreteNotionalSize: React.Dispatch<React.SetStateAction<number>>
  concreteLoadingAge: number
  setConcreteLoadingAge: React.Dispatch<React.SetStateAction<number>>
  concreteCementType: 'S' | 'N' | 'R'
  setConcreteCementType: React.Dispatch<React.SetStateAction<'S' | 'N' | 'R'>>
  concreteCreepCoefficient: number
  concreteEffectiveModulus: number
  showMaterialParams: boolean
  setShowMaterialParams: React.Dispatch<React.SetStateAction<boolean>>
  checkLateralTorsionalBuckling: boolean
  setCheckLateralTorsionalBuckling: React.Dispatch<React.SetStateAction<boolean>>
  reliabilityClass: 1 | 2 | 3
  setReliabilityClass: React.Dispatch<React.SetStateAction<1 | 2 | 3>>
  applyRc1Reduction: boolean
  setApplyRc1Reduction: React.Dispatch<React.SetStateAction<boolean>>
  imposedLoadCategory: ImposedLoadCategory
  setImposedLoadCategory: React.Dispatch<React.SetStateAction<ImposedLoadCategory>>
  materialPresets: MaterialPresets
  resolvedDensity: number
  calculateResolvedSelfWeight: (crossSection: CrossSection) => number
  materialFactorsDisplay: MaterialFactorsDisplay
  getAlphaCC: () => number
  fetchMaterialDetails: () => Promise<any | null>
  resolvingMaterial: boolean
  materialDetails: any | null
  steelInfoLine: string
  concreteInfoLine: string
  timberInfoLine: string
  sectionMaterialStrength: SectionMaterialStrength | null
  codeRequirementsPayload: Record<string, unknown>
}

interface UseMaterialStateParams {
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  getFilteredSectionPresets: (material: string) => Record<string, CrossSection>
  lockedMaterial: MaterialType
}

const useMaterialState = ({ model, setModel, getFilteredSectionPresets, lockedMaterial }: UseMaterialStateParams): UseMaterialStateResult => {
  // Material is fixed per page (concrete / wood / steel) and never changes after mount.
  const [currentMaterial] = useState<MaterialType>(lockedMaterial)
  const [materialSyncedFromModel, setMaterialSyncedFromModel] = useState<boolean>(false)
  const [nationalAnnex, setNationalAnnex] = useState<NationalAnnex>('Norwegian')

  const [selectedSteelGrade, setSelectedSteelGrade] = useState<SteelGrade>('S355')
  const [selectedSteelSubgrade, setSelectedSteelSubgrade] = useState<string>('J0')
  const [steelSubgrades, setSteelSubgrades] = useState<string[]>([])
  const [steelBands, setSteelBands] = useState<number[]>([])
  const [steelThickness, setSteelThickness] = useState<number | ''>('')

  const [selectedConcreteClass, setSelectedConcreteClass] = useState<ConcreteClass>('C30/37')
  const [selectedReinfClass, setSelectedReinfClass] = useState<ReinforcementClass>('B500NC')
  // Prestressing support is deferred – keep state internal but hide from UI and report
  const [selectedPrestressClass, setSelectedPrestressClass] = useState<PrestressClass>('Y1860')

  const [timberKind, setTimberKind] = useState<TimberKind>(model.materialSelection?.timberKind ?? 'glulam')
  const [selectedTimberClass, setSelectedTimberClass] = useState<string>(model.materialSelection?.timberClass ?? 'GL24h')
  const [timberOptions, setTimberOptions] = useState<TimberLibraryItem[]>([])
  const [timberKmod, setTimberKmod] = useState<number>(0.90)
  // kmod per load duration for the active service class, sourced from the backend
  // (EN 1995-1-1 Table 3.1) so the duration dropdown previews are never hardcoded.
  const [timberKmodTable, setTimberKmodTable] = useState<Record<string, number>>({})
  // Duration class of the Variable (imposed) loads. Default medium-term = "nyttelast på gulv"
  // per NS-EN 1995-1-1 NA Tabell NA.2.2; the governing kmod per combination is derived in the
  // backend from the shortest-duration action present (EC5 §3.1.3(2)).
  const [timberLoadDuration, setTimberLoadDuration] = useState<'permanent' | 'long-term' | 'medium-term' | 'short-term' | 'instantaneous'>(model.materialSelection?.timberLoadDuration ?? 'medium-term')
  const [timberServiceClass, setTimberServiceClass] = useState<1 | 2 | 3>(model.materialSelection?.timberServiceClass ?? 2)

  // Concrete creep parameters (EC2 Annex B)
  const [concreteRelativeHumidity, setConcreteRelativeHumidity] = useState<number>(50) // RH in %
  const [concreteNotionalSize, setConcreteNotionalSize] = useState<number>(200) // h0 in mm (2*Ac/u)
  const [concreteLoadingAge, setConcreteLoadingAge] = useState<number>(28) // t0 in days
  const [concreteCementType, setConcreteCementType] = useState<'S' | 'N' | 'R'>('N') // S=slow, N=normal, R=rapid

  const [showMaterialParams, setShowMaterialParams] = useState<boolean>(false)
  // Lateral-torsional buckling ("vipping") check. On by default; turn off when the construction
  // laterally restrains the compression flange so LTB cannot occur.
  const [checkLateralTorsionalBuckling, setCheckLateralTorsionalBuckling] = useState<boolean>(model.materialSelection?.checkLateralTorsionalBuckling ?? true)
  // Reliability class / Pålitelighetsklasse (NS-EN 1990 NA). Drives the KFI factor on the VARIABLE ULS
  // load factors only: RC1=0.9, RC2=1.0 (default), RC3=1.0 (no load increase — handled via control classes).
  const [reliabilityClass, setReliabilityClass] = useState<1 | 2 | 3>(2)
  // The RC1 reduction is permissive in the NA ("γF kan reduseres") — opt-out, only relevant for RC1.
  const [applyRc1Reduction, setApplyRc1Reduction] = useState<boolean>(true)

  // Imposed-load (occupancy) category — drives the backend's NA.A1.1 ψ lookup. Default A (boliger).
  const [imposedLoadCategory, setImposedLoadCategory] = useState<ImposedLoadCategory>('A')
  const [materialDetails, setMaterialDetails] = useState<any | null>(null)
  const [resolvingMaterial, setResolvingMaterial] = useState<boolean>(false)
  // Monotonic sequence for material-detail requests: only the latest request may commit
  // its response (see fetchMaterialDetails) — responses can arrive out of order, and a
  // selection change bumps it at commit time so the in-flight request is outdated at once.
  const materialRequestSequence = useRef<number>(0)

  // Loading a saved design replaces the model wholesale MID-SESSION, but the useState
  // initializers above only read model.materialSelection at mount. Adopt the loaded
  // selection into the UI state; the write-back effect below is a no-op once the two
  // agree (both sides compare before writing), so the pair cannot loop. Without this,
  // the write-back would overwrite a freshly loaded design's saved material selection
  // with whatever the UI happened to show.
  useEffect(() => {
    if (currentMaterial !== 'timber') {
      return
    }
    const selection = model.materialSelection
    if (!selection) {
      return  // older saved design without a selection: keep the current UI state
    }
    if (selection.timberKind !== undefined && selection.timberKind !== timberKind) {
      setTimberKind(selection.timberKind)
    }
    if (selection.timberClass !== undefined && selection.timberClass !== selectedTimberClass) {
      setSelectedTimberClass(selection.timberClass)
    }
    if (selection.timberLoadDuration !== undefined && selection.timberLoadDuration !== timberLoadDuration) {
      setTimberLoadDuration(selection.timberLoadDuration)
    }
    if (selection.timberServiceClass !== undefined && selection.timberServiceClass !== timberServiceClass) {
      setTimberServiceClass(selection.timberServiceClass)
    }
    if (selection.checkLateralTorsionalBuckling !== undefined && selection.checkLateralTorsionalBuckling !== checkLateralTorsionalBuckling) {
      setCheckLateralTorsionalBuckling(selection.checkLateralTorsionalBuckling)
    }
    // Deliberately keyed on the model's selection only: after a user edit the write-back
    // updates the model to match the state, so this re-runs and compares equal (no-op).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMaterial, model.materialSelection])

  // Material grade and EC5 assumptions are independent of the section dimensions. Keep them in
  // the persisted model so a selected example retains its actual material after a page reload.
  useEffect(() => {
    if (currentMaterial !== 'timber') {
      return
    }
    setModel(previous => {
      const selection = previous.materialSelection
      if (selection?.timberKind === timberKind &&
          selection.timberClass === selectedTimberClass &&
          selection.timberLoadDuration === timberLoadDuration &&
          selection.timberServiceClass === timberServiceClass &&
          selection.checkLateralTorsionalBuckling === checkLateralTorsionalBuckling) {
        return previous
      }
      return {
        ...previous,
        materialSelection: {
          ...selection,
          timberKind,
          timberClass: selectedTimberClass,
          timberLoadDuration,
          timberServiceClass,
          checkLateralTorsionalBuckling
        }
      }
    })
  }, [currentMaterial, timberKind, selectedTimberClass, timberLoadDuration, timberServiceClass, checkLateralTorsionalBuckling, setModel])

  const resolvedElasticModulus = useMemo(() => {
    if (currentMaterial === 'timber' && typeof materialDetails?.e0Mean === 'number') {
      return materialDetails.e0Mean
    }
    if (typeof materialDetails?.elasticModulus === 'number') {
      return materialDetails.elasticModulus
    }
    return MATERIAL_PRESETS[currentMaterial].E
  }, [currentMaterial, materialDetails])

  // DB density once the material has resolved; the MATERIAL_PRESETS value is the pre-resolve
  // bootstrap only (see constants.ts). Both the applied self-weight load and every self-weight
  // readout must come from here, so the UI can never show a density the analysis did not use.
  const resolvedDensity = useMemo<number>(() => {
    if (typeof materialDetails?.density === 'number') {
      return materialDetails.density
    }
    return MATERIAL_PRESETS[currentMaterial].density
  }, [currentMaterial, materialDetails])

  const calculateResolvedSelfWeight = useCallback((crossSection: CrossSection): number => {
    if (!Number.isFinite(crossSection.area)) {
      throw new Error(`Self-weight calculation failed: cross-section area is ${crossSection.area}, expected a finite value in m². Material '${currentMaterial}', section '${crossSection.name}' (${crossSection.width}×${crossSection.height} mm).`)
    }
    return calculateSelfWeightFromDensity(crossSection.area, resolvedDensity)
  }, [currentMaterial, resolvedDensity])

  // Material is locked to the page; release the cross-section sync effect after mount.
  useEffect(() => {
    if (materialSyncedFromModel) {
      return
    }
    setMaterialSyncedFromModel(true)
  }, [materialSyncedFromModel])

  useEffect(() => {
    // Skip updating cross-section if the model already has the correct material
    // (i.e., loaded from a template)
    if (!materialSyncedFromModel) {
      return
    }
    
    // If model's cross-section already has this material with actual dimensions, 
    // don't override it (preserve template geometry)
    const existingSection = model.crossSection
    if (existingSection && existingSection.material === currentMaterial && existingSection.area > 0) {
      // Just update self-weight for the existing section
      const selfWeightValue = calculateResolvedSelfWeight(existingSection)
      const targetE = resolvedElasticModulus

      // Check if update is actually needed to prevent infinite loops
      const needsUpdate = model.spans.some(span => {
        // Check E
        if (targetE && Math.abs(span.E - targetE) > 1.0) return true
        
        // Check Self Weight
        const swLoad = span.loads.find(l => l.id === `selfweight-span-${span.id}` || l.id.includes('selfweight'))
        if (!swLoad) return true // Need to add it
        
        return Math.abs(swLoad.uniformIntensity - (-selfWeightValue)) > 1e-3
      })

      if (!needsUpdate) {
        return
      }

      setModel(prev => ({
        ...prev,
        spans: prev.spans.map(span => {
          const baseLoads = span.loads ?? []
          const updatedLoads = baseLoads.map(load => {
            if (load.id === `selfweight-span-${span.id}` || load.id.includes('selfweight')) {
              return {
                ...load,
                uniformIntensity: -selfWeightValue,
                startIntensity: -selfWeightValue,
                endIntensity: -selfWeightValue
              }
            }
            return load
          })
          return {
            ...span,
            E: Number.isFinite(targetE) ? targetE : span.E,
            A: existingSection.area,
            I: existingSection.inertia,
            loads: updatedLoads
          }
        })
      }))
      return
    }
    
    const filteredSectionsObj = getFilteredSectionPresets(currentMaterial)
    const filteredSections = Object.values(filteredSectionsObj)
    const firstNonCustom = filteredSections.find(section => !section.isCustom)

    if (!firstNonCustom) {
      return
    }

    const updatedCrossSection: CrossSection = {
      name: firstNonCustom.name,
      width: firstNonCustom.width,
      height: firstNonCustom.height,
      area: firstNonCustom.area,
      inertia: firstNonCustom.inertia,
      isCustom: false,
      key: firstNonCustom.key,
      material: currentMaterial,
      sectionType: firstNonCustom.sectionType,
      webThickness: firstNonCustom.webThickness,
      flangeThickness: firstNonCustom.flangeThickness,
      ...calculateSectionProperties({
        name: firstNonCustom.name,
        width: firstNonCustom.width,
        height: firstNonCustom.height,
        area: firstNonCustom.area,
        inertia: firstNonCustom.inertia,
        isCustom: false,
        material: currentMaterial,
        sectionType: firstNonCustom.sectionType,
        webThickness: firstNonCustom.webThickness,
        flangeThickness: firstNonCustom.flangeThickness
      }, currentMaterial as 'steel' | 'concrete' | 'timber')
    }

    const updatedE = resolvedElasticModulus
    const selfWeightValue = calculateResolvedSelfWeight(updatedCrossSection)

    setModel(prev => ({
      ...prev,
      crossSection: updatedCrossSection,
      spans: prev.spans.map(span => {
        const baseLoads = span.loads ?? []
        const updatedLoads = baseLoads.map(load => {
          if (load.id === `selfweight-span-${span.id}` || load.id.includes('selfweight')) {
            return {
              ...load,
              uniformIntensity: -selfWeightValue,
              startIntensity: -selfWeightValue,
              endIntensity: -selfWeightValue,
              startPosition: 0,
              endPosition: span.length
            }
          }
          return load
        })

        const hasSelfWeight = updatedLoads.some(load => load.id === `selfweight-span-${span.id}` || load.id.includes('selfweight'))

        const loadsWithSelfWeight = hasSelfWeight
          ? updatedLoads
          : [
              ...updatedLoads,
              {
                id: `selfweight-span-${span.id}`,
                type: 'UniformLoad' as const,
                spanId: span.id,
                category: 'Permanent' as const,
                pointLoad: 0,
                pointLoadPosition: 0,
                normalForce: 0,
                normalForcePosition: 0,
                normalForceEccentricity: 0,
                uniformIntensity: -selfWeightValue,
                startIntensity: -selfWeightValue,
                endIntensity: -selfWeightValue,
                startPosition: 0,
                endPosition: span.length
              }
            ]

        return {
          ...span,
          E: Number.isFinite(updatedE) ? updatedE : span.E,
          A: updatedCrossSection.area,
          I: updatedCrossSection.inertia,
          loads: loadsWithSelfWeight
        }
      })
    }))
  }, [currentMaterial, getFilteredSectionPresets, setModel, materialSyncedFromModel, model.crossSection, model.spans, resolvedElasticModulus, calculateResolvedSelfWeight])

  useEffect(() => {
    if (currentMaterial !== 'concrete') {
      setModel(prev => {
        const crossSection = prev.crossSection
        const reinforcementResetNeeded =
          (crossSection.reinforcementRatio ?? 0) !== 0 ||
          (crossSection.reinforcementAreaBottom ?? 0) !== 0 ||
          crossSection.reinforcementLeverArmBottom !== undefined
        if (!reinforcementResetNeeded) {
          return prev
        }

        return {
          ...prev,
          crossSection: {
            ...crossSection,
            reinforcementRatio: undefined,
            reinforcementAreaBottom: undefined,
            reinforcementLeverArmBottom: undefined
          }
        }
      })
      return
    }

    const zones = model.rebarZones
    const crossSection = model.crossSection
    const widthMm = crossSection.width
    const heightMm = crossSection.height

    if (!zones || zones.length === 0 || !Number.isFinite(widthMm) || !Number.isFinite(heightMm) || widthMm <= 0 || heightMm <= 0) {
      setModel(prev => {
        const cs = prev.crossSection
        const needsUpdate =
          (cs.reinforcementRatio ?? 0) !== 0 ||
          (cs.reinforcementAreaBottom ?? 0) !== 0 ||
          cs.reinforcementLeverArmBottom !== undefined
        if (!needsUpdate) {
          return prev
        }
        return {
          ...prev,
          crossSection: {
            ...cs,
            reinforcementRatio: 0,
            reinforcementAreaBottom: 0,
            reinforcementLeverArmBottom: undefined
          }
        }
      })
      return
    }

    const zone = zones[0]
    const layers = zone.layers ?? []
    let areaSumMm2 = 0
    let weightedZ = 0
    let minZ = Infinity

    layers.forEach(layer => {
      const { z, diameter, count } = layer
      if (typeof z !== 'number' || typeof diameter !== 'number' || typeof count !== 'number') {
        return
      }
      if (z >= 0 || diameter <= 0 || count <= 0) {
        return
      }
      const areaMm2 = Math.PI * Math.pow(diameter, 2) / 4 * count
      areaSumMm2 += areaMm2
      weightedZ += areaMm2 * z
      if (z < minZ) {
        minZ = z
      }
    })

    let ratio = 0
    let areaM2 = 0
    let leverArm: number | undefined
    let effectiveCover = zone.cover

    if (areaSumMm2 > 0) {
      const zCentroid = weightedZ / areaSumMm2
      const depthMm = heightMm / 2 - zCentroid
      if (depthMm > 0) {
        areaM2 = areaSumMm2 * 1e-6
        ratio = areaM2 / ((widthMm / 1000) * (depthMm / 1000))
        leverArm = depthMm / 1000 * 0.9
        if (!Number.isFinite(leverArm) || leverArm <= 0) {
          leverArm = depthMm / 1000
        }
      }

      if (!Number.isFinite(effectiveCover) || effectiveCover <= 0) {
        const distanceToSurface = heightMm / 2 + (Number.isFinite(minZ) ? minZ : 0)
        effectiveCover = Math.max(0, distanceToSurface)
      }
    }

    if (!Number.isFinite(ratio) || ratio < 1e-6) {
      ratio = 0
    }
    if (!Number.isFinite(areaM2) || areaM2 < 1e-9) {
      areaM2 = 0
    }
    if (leverArm !== undefined && (!Number.isFinite(leverArm) || leverArm <= 0)) {
      leverArm = undefined
    }

    const normalizedCover = Number.isFinite(effectiveCover) && effectiveCover > 0 ? effectiveCover : undefined

    setModel(prev => {
      const cs = prev.crossSection
      const ratioDiff = Math.abs((cs.reinforcementRatio ?? 0) - ratio) > 1e-6
      const areaDiff = Math.abs((cs.reinforcementAreaBottom ?? 0) - areaM2) > 1e-8
      const leverDiff = (() => {
        const previousLever = cs.reinforcementLeverArmBottom
        if (previousLever === undefined && leverArm === undefined) {
          return false
        }
        if ((previousLever === undefined) !== (leverArm === undefined)) {
          return true
        }
        return previousLever !== undefined && leverArm !== undefined && Math.abs(previousLever - leverArm) > 1e-6
      })()
      const coverDiff = (() => {
        const previousCover = cs.effectiveCover
        if (previousCover === undefined && normalizedCover === undefined) {
          return false
        }
        if ((previousCover === undefined) !== (normalizedCover === undefined)) {
          return true
        }
        return previousCover !== undefined && normalizedCover !== undefined && Math.abs(previousCover - normalizedCover) > 1e-3
      })()

      if (!ratioDiff && !areaDiff && !leverDiff && !coverDiff) {
        return prev
      }

      return {
        ...prev,
        crossSection: {
          ...cs,
          reinforcementRatio: ratio,
          reinforcementAreaBottom: areaM2,
          reinforcementLeverArmBottom: leverArm,
          effectiveCover: normalizedCover ?? cs.effectiveCover
        }
      }
    })
  }, [currentMaterial, model.rebarZones, model.crossSection.width, model.crossSection.height, setModel])

  useEffect(() => {
    const grade = steelGrades[selectedSteelGrade]
    if (!grade) {
      return
    }
    setSteelSubgrades(grade.availableSubgrades)
    setSteelBands(grade.thicknessBands)
    if (!grade.availableSubgrades.includes(selectedSteelSubgrade)) {
      setSelectedSteelSubgrade(grade.availableSubgrades[0])
    }
  }, [selectedSteelGrade, selectedSteelSubgrade])

  const governingSteelThickness = useMemo(() => {
    if (currentMaterial !== 'steel') {
      return 0
    }
    return Math.max(model.crossSection.webThickness ?? 0, model.crossSection.flangeThickness ?? 0)
  }, [currentMaterial, model.crossSection.webThickness, model.crossSection.flangeThickness])

  useEffect(() => {
    if (currentMaterial !== 'steel' || steelBands.length === 0) {
      return
    }
    const targetThickness = governingSteelThickness > 0 ? governingSteelThickness : 16
    const sortedBands = [...steelBands].sort((a, b) => a - b)
    const matchingBand = sortedBands.find(band => band >= targetThickness) ?? sortedBands[sortedBands.length - 1]
    if (steelThickness !== matchingBand) {
      setSteelThickness(matchingBand)
    }
  }, [currentMaterial, governingSteelThickness, steelBands, steelThickness])

  // The timber catalogue (both kinds) is loaded once. Kind switches then pick a class from it
  // synchronously (selectTimberKind), so no committed render pairs a kind with the other kind's
  // class; the reconcile effect below covers the pair arriving inconsistent before the catalogue
  // is here (a switch during load, or a loaded design carrying an impossible pair).
  const [timberCatalogueError, setTimberCatalogueError] = useState<string | null>(null)
  useEffect(() => {
    if (currentMaterial !== 'timber') {
      return
    }
    let cancelled = false
    const loadCatalogue = async (): Promise<void> => {
      try {
        const list = await librariesApi.getTimber()
        if (!cancelled) {
          setTimberOptions(list)
        }
      } catch (error) {
        if (!cancelled) {
          setTimberCatalogueError(`Timber catalogue could not be loaded from /api/Libraries/materials/timber: ${error instanceof Error ? error.message : String(error)}`)
        }
      }
    }
    loadCatalogue()
    return () => { cancelled = true }
  }, [currentMaterial])

  const timberClassesOfKind = useCallback((kind: TimberKind): string[] => timberOptions.filter(option => option.kind === kind).map(option => option.class), [timberOptions])

  const timberSelectionStatus = useMemo<TimberSelectionStatus>(() => {
    if (currentMaterial !== 'timber') {
      return { state: 'ready' }
    }
    if (timberCatalogueError !== null) {
      return { state: 'error', message: timberCatalogueError }
    }
    if (timberOptions.length === 0) {
      return { state: 'pending' }
    }
    const classes = timberClassesOfKind(timberKind)
    if (classes.includes(selectedTimberClass)) {
      return { state: 'ready' }
    }
    if (classes.length === 0) {
      return { state: 'error', message: `No ${timberKind} classes in the materials DB (the timber catalogue has ${timberOptions.length} entries, none of kind '${timberKind}').` }
    }
    return { state: 'pending' }
  }, [currentMaterial, timberCatalogueError, timberOptions, timberClassesOfKind, timberKind, selectedTimberClass])

  useEffect(() => {
    if (timberSelectionStatus.state !== 'pending' || timberOptions.length === 0) {
      return
    }
    setSelectedTimberClass(timberClassesOfKind(timberKind)[0])
  }, [timberSelectionStatus, timberOptions, timberClassesOfKind, timberKind])

  const selectTimberKind = useCallback((kind: TimberKind): void => {
    setTimberKind(kind)
    const classes = timberClassesOfKind(kind)
    if (classes.length > 0 && !classes.includes(selectedTimberClass)) {
      setSelectedTimberClass(classes[0])
    }
  }, [timberClassesOfKind, selectedTimberClass])

  useEffect(() => {
    if (currentMaterial !== 'timber') {
      return
    }
    let cancelled = false
    const loadKmodTable = async () => {
      try {
        const table = await librariesApi.getTimberKmodTable(timberServiceClass)
        if (!cancelled) {
          setTimberKmodTable(table)
        }
      } catch {
        if (!cancelled) {
          setTimberKmodTable({})
        }
      }
    }
    loadKmodTable()
    return () => { cancelled = true }
  }, [currentMaterial, timberServiceClass])

  const getAlphaCC = useCallback(() => {
    return nationalAnnex === 'Norwegian' ? 0.85 : 1.0
  }, [nationalAnnex])

  // DB-authoritative partial factors / reference strengths for the current material, taken from the
  // resolved /design payload so the materials UI can never drift from the DB. γ are dimensionless;
  // strengths are MPa and moduli converted to GPa for display. Undefined until the payload loads.
  const materialFactorsDisplay = useMemo<MaterialFactorsDisplay>(() => {
    const details = materialDetails
    const asNumber = (value: unknown): number | undefined => typeof value === 'number' ? value : undefined
    const toGpa = (value: unknown): number | undefined => typeof value === 'number' ? value / 1e9 : undefined
    if (!details || details.error) {
      return {}
    }
    if (currentMaterial === 'steel') {
      return { steelGammaM0: asNumber(details.gammaM0), steelGammaM1: asNumber(details.gammaM1), steelGammaM2: asNumber(details.gammaM2) }
    }
    if (currentMaterial === 'concrete') {
      const reinforcement = details.reinforcement ?? {}
      const prestressing = details.prestressing ?? {}
      return {
        concreteGammaC: asNumber(details.gammaC),
        concreteAlphaCC: asNumber(details.alphaCC),
        concreteGammaS: asNumber(reinforcement.gammaS),
        concretePrestressGammaS: asNumber(prestressing.gammaS),
        reinforcementFyMpa: asNumber(reinforcement.fyk),
        reinforcementEsGpa: toGpa(reinforcement.elasticModulus),
        prestressFpkMpa: asNumber(prestressing.fpk),
        prestressEpGpa: toGpa(prestressing.elasticModulus)
      }
    }
    return { timberGammaM: asNumber(details.gammaM) }
  }, [currentMaterial, materialDetails])

  // kdef (EN 1995-1-1 Table 3.2) is derived server-side from the service class and returned in
  // the timber /design payload; the frontend only displays it — never recomputes the table.
  const timberCreepFactor = useMemo(() => {
    return typeof materialDetails?.kdef === 'number' ? materialDetails.kdef : 0
  }, [materialDetails])

  // kmod for the active (load duration, service class) likewise comes from the backend /design
  // payload (EN 1995-1-1 Table 3.1) — the same source the analysis checker uses.
  useEffect(() => {
    if (typeof materialDetails?.kmod === 'number') {
      setTimberKmod(materialDetails.kmod)
    }
  }, [materialDetails])

  // Concrete creep coefficient φ(∞,t0) per EN 1992-1-1 Annex B
  const concreteCreepCoefficient = useMemo(() => {
    if (currentMaterial !== 'concrete') return 0

    // Get fcm from concrete class (approximate: fcm = fck + 8 MPa)
    const fckMatch = selectedConcreteClass.match(/C(\d+)/)
    const fck = fckMatch ? parseFloat(fckMatch[1]) : 30
    const fcm = fck + 8 // Mean compressive strength in MPa

    const RH = concreteRelativeHumidity
    const h0 = concreteNotionalSize // mm
    const t0 = concreteLoadingAge // days

    // β(fcm) = 16.8 / √fcm
    const betaFcm = 16.8 / Math.sqrt(fcm)

    // β(t0) = 1 / (0.1 + t0^0.20)
    const betaT0 = 1.0 / (0.1 + Math.pow(t0, 0.20))

    // φRH depends on fcm and h0
    // For fcm ≤ 35 MPa: φRH = 1 + (1 - RH/100) / (0.1 * h0^(1/3))
    // For fcm > 35 MPa: φRH = (1 + (1 - RH/100) / (0.1 * h0^(1/3)) * α1) * α2
    // where α1 = (35/fcm)^0.7, α2 = (35/fcm)^0.2
    let phiRH: number
    if (fcm <= 35) {
      phiRH = 1.0 + (1.0 - RH / 100.0) / (0.1 * Math.pow(h0, 1.0 / 3.0))
    } else {
      const alpha1 = Math.pow(35.0 / fcm, 0.7)
      const alpha2 = Math.pow(35.0 / fcm, 0.2)
      phiRH = (1.0 + (1.0 - RH / 100.0) / (0.1 * Math.pow(h0, 1.0 / 3.0)) * alpha1) * alpha2
    }

    // φ(∞,t0) = φRH * β(fcm) * β(t0)
    const phi = phiRH * betaFcm * betaT0
    return phi
  }, [currentMaterial, selectedConcreteClass, concreteRelativeHumidity, concreteNotionalSize, concreteLoadingAge])

  // Effective modulus for long-term analysis: Ec,eff = Ecm / (1 + φ)
  const concreteEffectiveModulus = useMemo(() => {
    if (currentMaterial !== 'concrete' || !materialDetails?.elasticModulus) return 0
    const Ecm = materialDetails.elasticModulus // Pa
    const phi = concreteCreepCoefficient
    return Ecm / (1.0 + phi)
  }, [currentMaterial, materialDetails?.elasticModulus, concreteCreepCoefficient])

  const fetchMaterialDetails = useCallback(async (): Promise<any | null> => {
    // Monotonic request token: selections can change while a request is in flight (e.g.
    // loading a saved design mid-fetch), and responses are not guaranteed to arrive in
    // order. Only the LATEST request may commit materialDetails / clear the resolving
    // flag — otherwise a slow response for the previous class overwrites the current
    // one and self-weight/E silently follow the wrong material.
    const requestToken = materialRequestSequence.current + 1
    materialRequestSequence.current = requestToken
    const isLatest = (): boolean => materialRequestSequence.current === requestToken
    try {
      setResolvingMaterial(true)
      // Clear stale details so nothing derives values from the previous selection while
      // the matching response is still in flight.
      setMaterialDetails(null)

      if (currentMaterial === 'steel') {
        const thicknessForQuery = steelThickness || governingSteelThickness || 16
        const response = await fetch(`/api/Libraries/materials/steel/design?grade=${encodeURIComponent(selectedSteelGrade)}&subgrade=${encodeURIComponent(selectedSteelSubgrade)}&productForm=section&thickness=${thicknessForQuery}&annex=${encodeURIComponent(nationalAnnex)}`)
        if (!response.ok) {
          let errorMessage = `${response.status} ${response.statusText}`
          try {
            const errorJson = await response.json()
            if (errorJson.error) {
              errorMessage = errorJson.error
            }
          } catch {
            // If JSON parsing fails, keep the status text
          }
          throw new Error(`Steel material error: ${errorMessage}`)
        }
        const json = await response.json()
        if (isLatest()) {
          setMaterialDetails(json.data)
        }
        return json.data
      }

      if (currentMaterial === 'timber') {
        // Only reached with a catalogue-consistent kind/class pair (the auto-fetch effect withholds
        // the lookup otherwise). Send the selections (load duration + service class); the backend derives kmod from them
        // (EN 1995-1-1 Table 3.1), the same source the analysis checker uses. annex drives γM only
        // (NA.2.3 glulam 1.15 / solid 1.25 vs base-EC 1.25 / 1.30); strengths are annex-independent.
        const response = await fetch(`/api/Libraries/materials/timber/design?kind=${encodeURIComponent(timberKind)}&class=${encodeURIComponent(selectedTimberClass)}&loadDuration=${encodeURIComponent(timberLoadDuration)}&serviceClass=${timberServiceClass}&annex=${encodeURIComponent(nationalAnnex)}`)
        if (!response.ok) {
          let errorMessage = `${response.status} ${response.statusText}`
          try {
            const errorJson = await response.json()
            if (errorJson.error) {
              errorMessage = errorJson.error
            }
          } catch {
            // If JSON parsing fails, keep the status text
          }
          throw new Error(`Timber material error: ${errorMessage}`)
        }
        const json = await response.json()
        if (isLatest()) {
          setMaterialDetails(json.data)
        }
        return json.data
      }

      const [concreteResponse, reinforcementResponse, prestressResponse] = await Promise.all([
        fetch(`/api/Libraries/materials/concrete/design?class=${encodeURIComponent(selectedConcreteClass)}&annex=${encodeURIComponent(nationalAnnex)}`),
        fetch(`/api/Libraries/materials/reinforcement-steel/design?grade=${encodeURIComponent(selectedReinfClass)}&annex=${encodeURIComponent(nationalAnnex)}`),
        fetch(`/api/Libraries/materials/prestressing-steel/design?grade=${encodeURIComponent(selectedPrestressClass)}&annex=${encodeURIComponent(nationalAnnex)}`)
      ])

      if (!concreteResponse.ok) {
        let errorMessage = `${concreteResponse.status} ${concreteResponse.statusText}`
        try {
          const errorJson = await concreteResponse.json()
          if (errorJson.error) {
            errorMessage = errorJson.error
          }
        } catch {
          // If JSON parsing fails, use text response
          const errorText = await concreteResponse.text()
          if (errorText) errorMessage = errorText
        }
        throw new Error(`Concrete material error: ${errorMessage}`)
      }
      if (!reinforcementResponse.ok) {
        let errorMessage = `${reinforcementResponse.status} ${reinforcementResponse.statusText}`
        try {
          const errorJson = await reinforcementResponse.json()
          if (errorJson.error) {
            errorMessage = errorJson.error
          }
        } catch {
          const errorText = await reinforcementResponse.text()
          if (errorText) errorMessage = errorText
        }
        throw new Error(`Reinforcement steel error: ${errorMessage}`)
      }
      if (!prestressResponse.ok) {
        let errorMessage = `${prestressResponse.status} ${prestressResponse.statusText}`
        try {
          const errorJson = await prestressResponse.json()
          if (errorJson.error) {
            errorMessage = errorJson.error
          }
        } catch {
          const errorText = await prestressResponse.text()
          if (errorText) errorMessage = errorText
        }
        throw new Error(`Prestressing steel error: ${errorMessage}`)
      }

      const concreteJson = await concreteResponse.json()
      const reinforcementJson = await reinforcementResponse.json()
      const prestressJson = await prestressResponse.json()

      const details = {
        ...concreteJson.data,
        reinforcement: reinforcementJson.data,
        prestressing: prestressJson.data
      }
      if (isLatest()) {
        setMaterialDetails(details)
      }
      return details
    } catch (error) {
      console.error('Failed to resolve material:', error)
      if (isLatest()) {
        setMaterialDetails({ error: String(error) })
      }
      return null
    } finally {
      if (isLatest()) {
        setResolvingMaterial(false)
      }
    }
  }, [currentMaterial, selectedSteelGrade, selectedSteelSubgrade, steelThickness, governingSteelThickness, timberKind, selectedTimberClass, timberLoadDuration, timberServiceClass, nationalAnnex, selectedConcreteClass, selectedReinfClass, selectedPrestressClass])

  // A selection change must outdate the in-flight request at COMMIT time. The refetch below is a
  // passive effect, and between the commit and its run the stale response could still resolve,
  // pass isLatest() and commit the previous selection's details under the new selection. A
  // layout effect runs inside the commit, before any response can be delivered. Keyed on
  // fetchMaterialDetails because its identity changes exactly when a selection it reads changes.
  useLayoutEffect(() => {
    materialRequestSequence.current += 1
  }, [fetchMaterialDetails])

  // Auto-fetch material details when material selections change. While the timber kind/class
  // pair is not a catalogue pair the lookup is withheld and the state says so explicitly: details
  // null with resolving true (loading), or the error when it never can proceed. Either way the
  // in-flight request is outdated so its response cannot land on top of that state.
  useEffect(() => {
    if (timberSelectionStatus.state === 'ready') {
      fetchMaterialDetails()
      return
    }
    materialRequestSequence.current += 1
    if (timberSelectionStatus.state === 'pending') {
      setResolvingMaterial(true)
      setMaterialDetails(null)
      return
    }
    setResolvingMaterial(false)
    setMaterialDetails({ error: timberSelectionStatus.message })
  }, [fetchMaterialDetails, timberSelectionStatus])

  const steelInfoLine = useMemo(() => {
    const fallbackGrade = steelGrades[selectedSteelGrade]
    const modulus = typeof materialDetails?.elasticModulus === 'number' ? materialDetails.elasticModulus : fallbackGrade.E
    const density = typeof materialDetails?.density === 'number' ? materialDetails.density : 7850
    const yieldMpa = typeof materialDetails?.yieldStrength === 'number' ? materialDetails.yieldStrength : fallbackGrade.fy / 1e6
    const fuMinMpa = typeof materialDetails?.ultimateStrengthMin === 'number'
      ? materialDetails.ultimateStrengthMin
      : typeof materialDetails?.ultimateStrength === 'number'
        ? materialDetails.ultimateStrength
        : fallbackGrade.fu / 1e6
    const fuMaxMpa = typeof materialDetails?.ultimateStrengthMax === 'number' ? materialDetails.ultimateStrengthMax : fuMinMpa
    const yieldStrength = yieldMpa * 1e6
    const yieldText = yieldMpa.toFixed(0)
    const fuMinText = fuMinMpa.toFixed(0)
    const fuMaxText = fuMaxMpa.toFixed(0)
    const strainYield = yieldStrength / modulus
    let bandText = ''
    const resolvedThickness = typeof materialDetails?.thicknessMax === 'number' ? materialDetails.thicknessMax : steelThickness
    if (resolvedThickness !== '' && steelBands.length > 0) {
      const sortedBands = [...steelBands].sort((a, b) => a - b)
      const index = sortedBands.findIndex(band => band === resolvedThickness)
      if (index === 0) {
        bandText = ` t≤${sortedBands[0]}mm`
      } else if (index > 0) {
        const previous = sortedBands[index - 1]
        bandText = ` ${previous}<t≤${sortedBands[index]}mm`
      }
    }
    const subgrade = typeof materialDetails?.subgrade === 'string' ? materialDetails.subgrade : selectedSteelSubgrade
    const subgradeText = subgrade ? ` ${subgrade}` : ''
    return `${selectedSteelGrade}${subgradeText}${bandText} · E=${(modulus / 1e9).toFixed(0)} GPa · ρ=${density} kg/m³ · Fy≈${yieldText} MPa · Fu≈${fuMinText}–${fuMaxText} MPa · εy≈${strainYield.toFixed(4)}`
  }, [selectedSteelGrade, selectedSteelSubgrade, steelThickness, steelBands, materialDetails])

  const concreteInfoLine = useMemo(() => {
    const concrete = concreteClasses[selectedConcreteClass as keyof typeof concreteClasses]
    const modulus = typeof materialDetails?.elasticModulus === 'number' ? materialDetails.elasticModulus : concrete?.E ?? 33e9
    const density = typeof materialDetails?.density === 'number' ? materialDetails.density : concrete?.density ?? 2400
    const strength = typeof materialDetails?.fck === 'number' ? materialDetails.fck * 1e6 : concrete?.fck ?? 30e6
    const ultimateStrain = 0.0035
    return `${selectedConcreteClass} · E=${(modulus / 1e9).toFixed(1)} GPa · ρ=${density} kg/m³ · Fck=${(strength / 1e6).toFixed(0)} MPa · εcu≈${ultimateStrain.toFixed(4)}`
  }, [selectedConcreteClass, materialDetails])

  const timberInfoLine = useMemo(() => {
    const filtered = timberOptions.filter(option => option.kind === (timberKind === 'glulam' ? 'glulam' : 'solid'))
    const item = filtered.find(option => option.class === selectedTimberClass)
    if (item) {
      const modulus = item.e0Mean
      const density = Math.round(item.density)
      const fmK = item.fmK
      const strain = fmK * 1e6 / modulus
      return `${selectedTimberClass} · E=${(modulus / 1e9).toFixed(1)} GPa · ρ≈${density} kg/m³ · fm,k=${fmK.toFixed(0)} MPa · εu≈${strain.toFixed(4)}`
    }
    if (timberKind === 'glulam') {
      return 'GL24h · E≈11.5 GPa · ρ≈430 kg/m³ · fm,k=24 MPa · εu≈0.0021'
    }
    return 'C24 · E≈11.0 GPa · ρ≈420 kg/m³ · fm,k=24 MPa · εu≈0.0022'
  }, [timberOptions, timberKind, selectedTimberClass])

  const sectionMaterialStrength = useMemo<SectionMaterialStrength | null>(() => {
    const sectionMaterial = (model.crossSection.material || currentMaterial || '').toLowerCase()

    if (sectionMaterial === 'steel') {
      // designStrength is consumed in Pa (CrossSectionDetailsTable: fyd × W). fyd/yieldStrength
      // from the design endpoint are MPa, so convert to Pa.
      const resolvedFyd = typeof materialDetails?.fyd === 'number' ? materialDetails.fyd * 1e6 : undefined
      const resolvedFy = typeof materialDetails?.yieldStrength === 'number' ? materialDetails.yieldStrength * 1e6 : undefined
      const resolvedGammaM0 = typeof materialDetails?.gammaM0 === 'number' ? materialDetails.gammaM0 : undefined
      const designStrength = resolvedFyd
        ?? (resolvedFy && resolvedGammaM0 && resolvedGammaM0 > 0 ? resolvedFy / resolvedGammaM0 : undefined)
      if (!Number.isFinite(designStrength) || (designStrength ?? 0) <= 0) {
        return null
      }
      return {
        designStrength,
        characteristicYieldStrengthPa: resolvedFy,
        label: `Steel ${selectedSteelGrade} (fyd)`,
        materialName: `${selectedSteelGrade} ${selectedSteelSubgrade}`.trim()
      }
    }

    if (sectionMaterial === 'concrete') {
      const resolvedFcd = typeof materialDetails?.fcd === 'number' ? materialDetails.fcd * 1e6 : undefined
      const resolvedRebarFyd = typeof materialDetails?.reinforcement?.fyd === 'number' ? materialDetails.reinforcement.fyd * 1e6 : undefined
      const designStrength = resolvedFcd
      if (!Number.isFinite(designStrength) || (designStrength ?? 0) <= 0) {
        return null
      }
      // ε_cu2 (concrete) and ε_ud (rebar) come from the backend design endpoints
      // (authoritative, NA-aware); undefined until materialDetails is resolved.
      const concreteUltimateStrain: number | undefined = typeof materialDetails?.epsilonCu2 === 'number' ? materialDetails.epsilonCu2 : undefined
      const rebarDesignStrain: number | undefined = typeof materialDetails?.reinforcement?.epsilonUd === 'number' ? materialDetails.reinforcement.epsilonUd : undefined
      return {
        designStrength,
        label: `Concrete ${selectedConcreteClass} (fcd)` ,
        reinforcementDesignStrength: resolvedRebarFyd,
        reinforcementLabel: `Rebar ${selectedReinfClass} (fyd)`,
        materialName: selectedConcreteClass,
        concreteUltimateStrain,
        rebarDesignStrain
      }
    }

    if (sectionMaterial === 'timber') {
      // Prefer the backend-resolved design bending strength fm,d (correct per-class γM and the
      // kmod derived from load duration + service class). materialDetails.fb_d is in MPa.
      if (typeof materialDetails?.fb_d === 'number' && materialDetails.fb_d > 0) {
        return {
          designStrength: materialDetails.fb_d * 1e6,
          label: `Timber ${selectedTimberClass} (fm,d)`,
          materialName: selectedTimberClass
        }
      }
      return null
    }

    return null
  }, [model.crossSection.material, currentMaterial, selectedSteelGrade, selectedSteelSubgrade, selectedConcreteClass, selectedReinfClass, selectedTimberClass, materialDetails])

  const codeRequirementsPayload = useMemo(() => {
    const code = currentMaterial === 'steel' ? 'EN1993' : currentMaterial === 'concrete' ? 'EN1992' : 'EN1995'
    const codeVersion = currentMaterial === 'steel' ? 'EN 1993-1-1' : currentMaterial === 'concrete' ? 'EN 1992-1-1' : 'EN 1995-1-1'

    const ulsFactors: Record<string, number | undefined> = {
    }

    const slsFactors: Record<string, number | string> = {
      deflection_Span_Ratio: 250,
      // Cantilever spans are checked backend-side against 2·ℓ with these ratios (EC5 Table 7.2).
      deflection_Span_Ratio_Instantaneous: 300,  // Timber instantaneous deflection (shorter-term)
      // With no camber modelled, wfin = wnet,fin, so the final check must satisfy the EC5 Table 7.2
      // wnet,fin guidance (ℓ/250–350) — not only the laxer wfin range (ℓ/150–300).
      deflection_Span_Ratio_Final: 250,
      crack_Width_Limit: 0.3,
      // Timber creep parameters
      timberServiceClass: currentMaterial === 'timber' ? timberServiceClass : undefined,
      // Concrete creep parameters
      concreteRelativeHumidity: currentMaterial === 'concrete' ? concreteRelativeHumidity : undefined,
      concreteNotionalSize: currentMaterial === 'concrete' ? concreteNotionalSize : undefined,
      concreteLoadingAge: currentMaterial === 'concrete' ? concreteLoadingAge : undefined,
      concreteCementType: currentMaterial === 'concrete' ? concreteCementType : undefined,
      concreteCreepCoefficient: currentMaterial === 'concrete' ? concreteCreepCoefficient : undefined,
      concreteEffectiveModulus: currentMaterial === 'concrete' ? concreteEffectiveModulus : undefined,
      stress_Concrete_Quasi: 0.45,
      stress_Steel_Quasi: 0.8
      // stress_Timber_Quasi intentionally NOT defaulted: it is a project-specific rule, not an EC5
      // clause — enabling it silently would let a non-code criterion drive the SLS verdict. Set it
      // explicitly when a project adopts such a limit; the backend then runs the check.
    }

    return {
      code,
      nationalAnnex,
      year: 2024,
      codeVersion,
      materialType: currentMaterial,
      checkLateralTorsionalBuckling,
      reliabilityClass,
      applyRc1Reduction,
      imposedLoadCategory,
      uls: ulsFactors,
      sls: slsFactors
    }
  }, [currentMaterial, nationalAnnex, checkLateralTorsionalBuckling, reliabilityClass, applyRc1Reduction, imposedLoadCategory, timberServiceClass, concreteRelativeHumidity, concreteNotionalSize, concreteLoadingAge, concreteCementType, concreteCreepCoefficient, concreteEffectiveModulus])

  return {
    currentMaterial,
    nationalAnnex,
    setNationalAnnex,
    selectedSteelGrade,
    setSelectedSteelGrade,
    selectedSteelSubgrade,
    setSelectedSteelSubgrade,
    steelSubgrades,
    steelBands,
    steelThickness,
    setSteelThickness,
    selectedConcreteClass,
    setSelectedConcreteClass,
    selectedReinfClass,
    setSelectedReinfClass,
    selectedPrestressClass,
    setSelectedPrestressClass,
    timberKind,
    selectTimberKind,
    selectedTimberClass,
    setSelectedTimberClass,
    timberOptions,
    timberKmod,
    setTimberKmod,
    timberKmodTable,
    timberLoadDuration,
    setTimberLoadDuration,
    timberServiceClass,
    setTimberServiceClass,
    timberCreepFactor,
    concreteRelativeHumidity,
    setConcreteRelativeHumidity,
    concreteNotionalSize,
    setConcreteNotionalSize,
    concreteLoadingAge,
    setConcreteLoadingAge,
    concreteCementType,
    setConcreteCementType,
    concreteCreepCoefficient,
    concreteEffectiveModulus,
    showMaterialParams,
    setShowMaterialParams,
    checkLateralTorsionalBuckling,
    setCheckLateralTorsionalBuckling,
    reliabilityClass,
    setReliabilityClass,
    applyRc1Reduction,
    setApplyRc1Reduction,
    imposedLoadCategory,
    setImposedLoadCategory,
    materialPresets: MATERIAL_PRESETS,
    resolvedDensity,
    calculateResolvedSelfWeight,
    materialFactorsDisplay,
    getAlphaCC,
    fetchMaterialDetails,
    resolvingMaterial,
    materialDetails,
    steelInfoLine,
    concreteInfoLine,
    timberInfoLine,
    sectionMaterialStrength,
    codeRequirementsPayload
  }
}

export default useMaterialState

